import { Eye, UserPlus } from 'lucide-react'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsHeader,
  TabsHeaderActions,
  TabsHeaderTitle,
} from '../ui'
import { useMemo } from 'react'
import { getUserRoleLabel, type UserRole } from '../../types/auth'

export interface UsersHeaderProps {
  title?: string
  selectedRole: string
  onRoleChange: (role: string) => void
  showViewPermissionsButton?: boolean
  showCreateButton?: boolean
  showAddExistingButton?: boolean
  onViewPermissionsClick?: () => void
  onCreateClick?: () => void
  onAddExistingClick?: () => void
  availableRoleFilters?: string[]
  allowedRoles?: UserRole[]
}

export function UsersHeader({
  title = 'Users',
  selectedRole,
  onRoleChange,
  showViewPermissionsButton = false,
  showCreateButton = false,
  showAddExistingButton = false,
  onViewPermissionsClick,
  onCreateClick,
  onAddExistingClick,
  availableRoleFilters = ['TRADER', 'GROUP_OWNER', 'SUPER_ADMIN', 'COMPLIANCE'],
  allowedRoles,
}: UsersHeaderProps) {
  // Pre-compute available role options to avoid includes() on every render
  const roleOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Roles' }]

    if (availableRoleFilters.includes('TRADER')) {
      options.push({ value: 'TRADER', label: 'Trader' })
    }
    if (availableRoleFilters.includes('GROUP_OWNER')) {
      options.push({ value: 'GROUP_OWNER', label: 'Group Owner' })
    }
    if (availableRoleFilters.includes('SUPER_ADMIN')) {
      options.push({ value: 'SUPER_ADMIN', label: 'Super Admin' })
    }
    if (availableRoleFilters.includes('COMPLIANCE')) {
      options.push({ value: 'COMPLIANCE', label: 'Compliance' })
    }

    return options
  }, [availableRoleFilters])

  const specificActionTitle = useMemo(() => {
    if (allowedRoles && allowedRoles.length === 2) {
      return getUserRoleLabel(allowedRoles[0])
    }
    return null
  }, [allowedRoles])

  return (
    <TabsHeader className="flex-wrap">
      <TabsHeaderTitle>{title}</TabsHeaderTitle>
      <TabsHeaderActions className="flex-wrap md:flex-wrap gap-2">
        {showViewPermissionsButton && onViewPermissionsClick && (
          <Button onClick={onViewPermissionsClick} variant="main_outline" className="min-w-[180px]">
            <Eye className="h-3 md:h-4 w-3 md:w-4" />
            <span>
              <span className="hidden md:inline">View</span> Permissions
            </span>
          </Button>
        )}
        {showCreateButton && onCreateClick && (
          <Button
            onClick={onCreateClick}
            variant="main_action"
            className="px-1 md:px-2 min-w-[150px]"
          >
            <UserPlus className="h-3 md:h-4 w-3 md:w-4" />
            <span>
              Create <span className="hidden md:inline">{'User'}</span>
            </span>
          </Button>
        )}
        {showAddExistingButton && onAddExistingClick && (
          <Button
            onClick={onAddExistingClick}
            variant="main_action"
            className="min-w-[130px] md:min-w-[180px]"
          >
            <UserPlus className="h-3 md:h-4 w-3 md:w-4" />
            <span>
              Assign{' '}
              <span className="hidden md:inline">
                {specificActionTitle ? specificActionTitle : 'User'}
              </span>
            </span>
          </Button>
        )}
        {availableRoleFilters.length ? (
          <Select value={selectedRole} onValueChange={onRoleChange}>
            <SelectTrigger className="w-[180px] cursor-pointer rounded-xl bg-white">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </TabsHeaderActions>
    </TabsHeader>
  )
}
